import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../../utils/common";
import useUserInfo from "../../../hooks/useUserInfo";

const UploadAgreementModal = ({
  open,
  onClose,
  borrowerInfo,
  setBorrowerInfo,
}: any) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const privateInstance = useAxiosInstance();
  const user = useUserInfo();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      errorToast("Max allowed file size is 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setFileName(file.name);
    setUploading(true);
    setUploadProgress(0);

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V1}/orders/reports/upload?created_by=${user?.user_id}&report_type=borrower`,
        formData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              setUploadProgress(Math.round((e.loaded * 100) / e.total));
            }
          },
        },
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setFileUrl(res.data.response?.url);
        successToast("File uploaded successfully");
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!fileUrl || !fileName) return;

    const existingCreditDetails = borrowerInfo?.credit_details || {};
    const existingAgreements =
      borrowerInfo?.credit_details?.agreement_url || [];

    const normalizedAgreements = existingAgreements.map((item: any) =>
      typeof item === "string"
        ? { url: item, name: item.split("/").pop() }
        : item,
    );

    const newAgreement = {
      url: fileUrl,
      name: fileName,
    };

    const updatedAgreements = [
      ...normalizedAgreements.filter((a: any) => a.url !== newAgreement.url),
      newAgreement,
    ];

    const payload = {
      borrower_id: borrowerInfo?.borrower_id,
      credit_details: {
        ...existingCreditDetails,
        agreement_url: updatedAgreements,
      },
    };

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        successToast("Agreement added successfully");

        // ✅ Update local state
        setBorrowerInfo((prev: any) => ({
          ...prev,
          credit_details: {
            ...prev.credit_details,
            agreement_url: updatedAgreements,
          },
        }));

        onClose();
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleRemove = () => {
    setFileUrl(null);
    setFileName(null);
    setUploadProgress(0);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <CustomDialogTitle id="customized-dialog-title" onClose={onClose}>
        Upload Agreement
      </CustomDialogTitle>

      <DialogContent>
        {!fileUrl && (
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            sx={{
              border: "2px dashed #3f6ee8",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: uploading ? "default" : "pointer",
              backgroundColor: "#fafbff",
            }}
          >
            {uploading ? (
              <>
                <CircularProgress
                  variant="determinate"
                  value={uploadProgress}
                />
                <Typography mt={1} fontWeight={500}>
                  Uploading… {uploadProgress}%
                </Typography>
                {fileName && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {fileName}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 48, color: "#3f6ee8" }}
                />
                <Typography mt={1} fontWeight={500}>
                  Click to upload or drag & drop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF / Image • Max 10 MB
                </Typography>
              </>
            )}

            <input
              ref={inputRef}
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={(e) =>
                e.target.files && handleFileUpload(e.target.files[0])
              }
            />
          </Box>
        )}

        {fileUrl && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              px: 2,
              pb: 2,
              border: "1px solid #d6d9de",
              borderRadius: 2,
              position: "relative",
              backgroundColor: "#fafafa",
            }}
          >
            {isPdf ? (
              <Box display="flex" alignItems="center" gap={1}>
                <PictureAsPdfIcon color="error" />
                <Box flex={1}>
                  <Typography fontWeight={500}>{fileName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    PDF Document
                  </Typography>
                </Box>
                <LoadingButton
                  size="small"
                  variant="outlined"
                  onClick={() => window.open(fileUrl, "_blank")}
                >
                  View
                </LoadingButton>
                <IconButton size="small" onClick={handleRemove}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <Typography fontWeight={500} mb={1}>
                  {fileName}
                </Typography>
                <img
                  src={fileUrl}
                  alt="Agreement Preview"
                  style={{
                    width: "100%",
                    maxHeight: 300,
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={1}
                  sx={{
                    mt: 2,
                  }}
                >
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(fileUrl, "_blank")}
                  >
                    View Full Image
                  </LoadingButton>

                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleRemove}
                  >
                    Remove
                  </LoadingButton>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box textAlign="center" mt={3}>
          <LoadingButton
            variant="contained"
            disabled={!fileUrl}
            onClick={handleUpdate}
          >
            Update
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadAgreementModal;
